import {
  EVariableType,
  IdFormat,
  ScopeSectionVariableIds,
  ChangedNameVariableIds,
  EConfigitProperty,
} from '../data/Constants';
import {
  getNameWithCode,
  getSectionInfo,
  getVariableType,
  getViewIdSuffix,
  isSubModelAssigned,
  getSubModelsFromSectionId,
  checkIsPasswordFeature,
  checkIsMarkedOptional,
  getFeatureId,
  removeViewSuffix,
} from './ConfigurationDataHelperFunctions';
import { t } from 'i18next';
import { isUndefinedOrEmpty } from './Util';
import { AppStore } from '../store/AppStore';

import type {
  IConfigurationValue,
  IConfigurationVariable,
  IFeatureOrFamily,
  ISection,
  ISectionPrice,
  IExtendedConfigureResponse,
  IDetailedSummary,
} from '../../types';
import { getVariableSalesText } from './SalesTextHelperFunctions';

/**
 * Returns the user-entered value stored for a numeric optional variable, if any.
 * Reads `optionalItems` from configuration state; returns undefined for non-numeric
 * variables or when no `IOptionalNumericValue` entry exists.
 */
function getStoredOptionalNumericValue(variable: IConfigurationVariable): string | undefined {
  if (variable?.variableType !== EVariableType.Number) return undefined;
  const item = AppStore.getState().configuration.savedConfiguration?.optionalItems?.get(
    variable.id
  );
  if (item && typeof item === 'object' && 'value' in item) return item.value;
  return undefined;
}

/**
 * Creates an empty `ISectionPrice` shell for a section with reset aggregates.
 * @param {ISectionPrice} sec - The source section.
 * @returns {ISectionPrice} A new section with empty children/variables and zero price.
 */
function createEmptySection(
  sec: ISectionPrice,
  shortSalesText: Map<string, Map<string, string>>
): ISectionPrice {
  // New section shell: we reset derived fields and keep original metadata
  if (isSubModel(sec)) {
    const id = removeViewSuffix(sec.id);
    sec.name = getVariableSalesText(shortSalesText, id) ?? sec.name;
  }
  return {
    ...sec,
    // Explicit array types avoid TS inferring `never[]` and help Sonar
    sections: [] as ISectionPrice[],
    variables: [] as IConfigurationVariable[],
    price: 0,
  } as ISectionPrice;
}

/**
 * Creates an optional-content holder for a section with accumulated price fields.
 * @param {ISectionPrice} sec - The source section.
 * @returns {ISectionPrice} An optional section container for variables and subsections.
 */
function createEmptyOptionalSection(sec: ISectionPrice): ISectionPrice {
  // Holder for optional items (variables and subsections) with accumulated price
  return {
    id: `${sec.id}-optional`,
    sections: [] as ISectionPrice[],
    variables: [] as IConfigurationVariable[],
  } as ISectionPrice;
}

/**
 * Determines if a variable should be kept (shown and priced) in the non-optional summary.
 * @param {IConfigurationVariable} v - The variable to check.
 * @returns {boolean} True if variable is non-optional, visible, and not suppressed.
 */
function shouldKeepVariable(v: IConfigurationVariable): boolean {
  // Keep only non-optional, visible, non-suppressed, non-count, non-password variables
  return (
    !!v &&
    !v.isMarkedOptional &&
    !!v.values?.length &&
    !isSubmodelCountVariable(v as unknown as IConfigurationVariable) &&
    !v.suppressed &&
    !checkIsPasswordFeature(v as unknown as IConfigurationVariable)
  );
}

/**
 * Splits variables into kept (non-optional) and optional, and accumulates their prices.
 * @param {ISectionPrice} sec - The section whose variables are processed.
 * @returns {{ kept: IConfigurationVariable[], keptPrice: number, optionalVars: IConfigurationVariable[], optionalPrice: number }} Result with arrays and price totals.
 */
function processVariables(sec: ISectionPrice) {
  // Splits variables into kept (non-optional) and optional, and accumulates prices
  const variables: IConfigurationVariable[] = [];
  let variablePrice = 0;
  const optionalVariables: IConfigurationVariable[] = [];

  for (const v of sec.variables || []) {
    // Extract optional variable (if any) and add isMarkedOptional flag to variable and values
    //In case of string and number isMarkedOptional is added on variable level, for other types it is added on value level
    const optVar = appendIsMarkedOptionalVariable(v);
    if (optVar) {
      optionalVariables.push(optVar);
    }

    if (shouldKeepVariable(v)) {
      // Keep only assigned, non-optional, non-suppressed values for pricing/display
      const filteredVariable: IConfigurationVariable = {
        ...v,
        values: (v.values || []).filter((val) => isValidAssignedValue(val) && !val.suppressed),
      };
      // Aggregate price of filtered variable
      const varPrice = appendVariablePrice(filteredVariable) ?? 0;
      variables.push(filteredVariable);
      variablePrice += varPrice;
    }
  }

  return { variables, variablePrice, optionalVariables };
}

/**
 * Recursively builds child sections, aggregates their prices and optional content.
 * @param {ISectionPrice} sec - The section whose children to process.
 * @returns {{ children: ISectionPrice[], childPrice: number, optionalSections: ISectionPrice[], optionalPrice: number }} Result with built children and price totals.
 */
function processChildren(sec: ISectionPrice, shortSalesText: Map<string, Map<string, string>>) {
  // Recursively build child sections, accumulate child price (excluding submodel/scope),
  // and collect optional child content into a parallel optional structure
  const children: ISectionPrice[] = [];
  const optionalSections: ISectionPrice[] = [];
  let childPrice = 0;

  for (const child of sec.sections || []) {
    const builtChild = prepareConfigurationSummaryData(child, shortSalesText);
    if (!builtChild) continue;
    children.push(builtChild);

    if (!(isSubModel(child as unknown as ISection) || isScope(child as unknown as ISection))) {
      childPrice += builtChild.price || 0;
    }

    if (builtChild.optional) {
      // Flatten optional content from child into current optional structure
      optionalSections.push({
        ...builtChild,
        sections: builtChild.optional.sections || [],
        variables: builtChild.optional.variables || [],
      });
    }
  }
  return { children, childPrice, optionalSections };
}

/**
 * Returns a comma separated string of assigned values for the variable
 * @param {IConfigurationVariable } variable the variable to check for assigned values
 * @param {boolean} showCode to get showCode
 * @param {boolean} pageType to get Page Type
 * @returns {string} the comma separated string of assigned values
 */
export function getValueFromVariable(
  variable: IConfigurationVariable,
  showCode: boolean,
  pageType?: boolean
) {
  if (!variable?.values) {
    return [];
  }

  pageType ??= true;

  showCode =
    showCode &&
    variable.variableType !== EVariableType.String &&
    variable.variableType !== EVariableType.Number &&
    variable.variableType !== EVariableType.Date;

  // For a numeric optional, return only the user-entered value stored in state.
  const storedOptional = getStoredOptionalNumericValue(variable);
  if (storedOptional !== undefined) {
    const isPriceRelevant = variable?.pdmProperties?.priceRelevant ?? false;
    return [
      {
        // Use optional numeric value directly.
        name: storedOptional,
        price: isPriceRelevant ? variable.price : null,
        bundleInfo: variable.bundleInfo,
        isMarkedOptional: true,
        shortSalesText: variable.shortSalesText ?? undefined,
      },
    ];
  }

  // Filtering assigned values and setting price and name to return array of assigned values with price
  return variable.values
    .reduce<IConfigurationValue[]>(
      (valuesArray, value) => geValidValueFromVariable(value, variable, valuesArray),
      []
    )
    .map((a) => {
      //get the price relevant (IPR) property value for variable or vslue
      let isPriceRelevant = a?.pdmProperties?.priceRelevant ?? false;

      // geting value name and price from the variable data
      const value = {
        name: getNameWithCode(a as IFeatureOrFamily, showCode, pageType),
        price: isPriceRelevant ? a.price : null,
        bundleInfo: a.bundleInfo,
        isMarkedOptional: a.isMarkedOptional,
        shortSalesText: a.shortSalesText,
      };

      if (
        variable.variableType === EVariableType.String ||
        variable.variableType === EVariableType.Number
      ) {
        isPriceRelevant = variable?.pdmProperties?.priceRelevant ?? false;
        value.price = isPriceRelevant ? variable.price : null;
      }
      return value;
    });
}

/**
 * Returns list of selected values for a given variable
 * @param {IConfigurationVariable | IConfigurationValue} value the value to check
 * @param {IConfigurationVariable | IConfigurationValue} variable the variable to check
 * @param {Array<IConfigurationValue>} valuesArray array of selected values
 * @returns {Array<IConfigurationValue>} the name to be shown in the summary pane
 */
function geValidValueFromVariable(
  value: IConfigurationValue,
  variable: IConfigurationVariable,
  valuesArray: Array<IConfigurationValue>
): IConfigurationValue[] {
  if (
    variable?.variableType === EVariableType.Multiselect ||
    variable?.variableType === EVariableType.Singleselect
  ) {
    if (isAssignedOrOptionalValue(value, variable)) {
      valuesArray.push(value);
    }
  } else {
    const featureOrOptionId = getFeatureId(value.value, variable);
    const isOptional = checkIsMarkedOptional(featureOrOptionId, variable);
    if ((isOptional && !valuesArray.length) || isValidAssignedValue(value)) {
      valuesArray.push(value);
    }
  }
  return valuesArray;
}

/**
 * Returns the name to be shown in the summary pane for the given variable
 * @param {IConfigurationVariable} variable the variable to check
 * @param {boolean} showCode to showCode flag
 * @param {boolean} isSimpleConfigurationSummary isSimpleConfigurationSummary flag
 * @returns {string} the name to be shown in the summary pane
 * Returns variable name as eptmpty if feature flag SimpleConfigurationSummary is active and type of the variable in not String or Number
 * For type String and Number, variable name will be returned even if feature flag SimpleConfigurationSummary is active
 * If feature flag SimpleConfigurationSummary is not active, method returns variable name for all types
 */
export const getSummaryVariableName = (
  variable: IConfigurationVariable,
  showCode: boolean,
  isSimpleConfigurationSummary: boolean
): string => {
  if (
    isSimpleConfigurationSummary &&
    variable.variableType !== EVariableType.String &&
    variable.variableType !== EVariableType.Number
  ) {
    return '';
  }

  const id = getVariableIdWithoutPrefixes(variable);
  if (ChangedNameVariableIds.includes(id)) {
    return getNameWithCode(
      { ...variable, name: t('labels.' + id) } as IFeatureOrFamily,
      showCode,
      true
    );
  }
  return getNameWithCode(variable as IFeatureOrFamily, showCode, true);
};

/**
 * Checks if any variable inside the hierarchy of the given section is assigned
 * @param {ISection} section the section to check
 * @returns {boolean} true if section contains assigned value
 */
export function isSectionSelected(section: ISection): boolean {
  if (!section) {
    return false;
  }
  //removing scope for submodels
  if (isSubModel(section)) {
    section.sections = section.sections?.filter((sec: ISection) => !isScope(sec));
  }
  //check if a variable has assigned values
  for (const variable of section.variables) {
    if (isSubmodelCountVariable(variable)) {
      continue;
    }

    const assignedValues = variable.values.filter((value) =>
      isAssignedOrOptionalValue(value, variable)
    );
    if (assignedValues.length > 0) {
      return true;
    }
  }

  //check if a subsection is selected
  if (section.sections.some((s) => isSectionSelected(s))) {
    return true;
  }

  return false;
}

/**
 * Goes through all sections and returns a new list with selected sections only, unselected subsections will be not included.
 * @param {ISection[]} sections the sections to iterate
 * @returns {ISection[]} a new list with selected sections only
 */
export function getOnlySelectedSections(sections: ISection[] | undefined) {
  if (!sections) {
    return [];
  }

  const result = [];
  for (const section of sections) {
    if (!isSubModel(section) && isEmptySection(section)) {
      continue;
    }

    result.push(section);
  }

  return result;
}

/**
 * Recursive method to keep only subsections which have at least one assignment.
 * @param {ISection} section the section to check
 * @returns {boolean} true if section is not selected.
 */
function isEmptySection(section: ISection) {
  if (isSectionSelected(section)) {
    const result = [];

    for (const subSection of section.sections) {
      if (isEmptySection(subSection)) {
        continue;
      }

      result.push(subSection);
    }

    section.sections = result;
    return false;
  }
  return true;
}

/**
 * Calculates the total price based on the variables inside the sections
 * @param {ISectionPrice} rootSection an array of all sections
 * @param {IExtendedConfigureResponse} configuration the configuration
 * @returns {number} the total price
 */
export function calculatePrice(
  rootSection: ISectionPrice,
  configuration: IExtendedConfigureResponse
) {
  let totalPrice = rootSection.price;
  rootSection.sections
    .filter(
      (s: ISection) =>
        isSubModel(s) && isSubModelAssigned(configuration, getSubModelsFromSectionId(s.id))
    )
    .forEach((sec: ISectionPrice) => {
      totalPrice = totalPrice + calculatePrice(sec, configuration);
    });
  if (rootSection.basePrice) {
    totalPrice = totalPrice + rootSection.basePrice;
  }
  return totalPrice;
}

/**
 * Checks if the variable should be priced
 * @param {IConfigurationVariable} variable the variable to check
 * @returns {boolean} true, if variable should be priced
 */
export const isVariablePriced = (variable: IConfigurationVariable) =>
  variable.id && !ScopeSectionVariableIds.includes(getVariableIdWithoutPrefixes(variable));

/**
 * Checks if the section represents a submodel
 * @param {ISection} section the section to be checked
 * @returns {boolean} true if current section represents a submodel
 */
export const isSubModel: (section: ISection) => boolean = (section: ISection) => {
  const sectionParts = section.id.split('.');
  return (
    sectionParts?.length ===
      sectionParts.filter((sec: string) => getSectionInfo(sec).type === 'model').length &&
    sectionParts.length > 1
  );
};

/**
 * Checks if the section represents a scope section
 * @param {ISection} section the section to be checked
 * @returns {boolean} true if current section represents a scope section
 */
export const isScope: (section: ISection) => boolean = (section: ISection) =>
  getSectionInfo(section.id).type === 'scope';

/**
 * Checks if the section represents has a valid section id
 * @param {ISection} section the section to be checked
 * @returns  {boolean} true if current section has a valid section id
 */
export const isSection: (section: ISection) => boolean = (section: ISection) =>
  getSectionInfo(section.id).type === 'section';

/**
 * Returns the variable id without any prefixes
 * @param {IConfigurationVariable} variable the variable to check
 * @returns {string} the variable id without prefixes
 */
export const getVariableIdWithoutPrefixes = (variable: IConfigurationVariable) =>
  variable.id.split('.').at(-1);

/**
 * Checks if the value is assigned, not zero and not a LESS feature
 * @param {IConfigurationValue} value the value to check
 * @returns {boolean} true if the value is assigned, not zero and not a LESS feature
 */
const isValidAssignedValue = (value: IConfigurationValue): boolean =>
  !isUndefinedOrEmpty(value.value) && !!value.assigned && value.value !== 0;

/**
 * Checks if the value is assigned, not zero and not a LESS feature
 * @param {IConfigurationValue } value the value to check
 * @param {IConfigurationVariable } variable the value to check
 * @returns {boolean} true if the value is assigned, not zero and not a LESS feature
 */
const isAssignedOrOptionalValue = (
  value: IConfigurationValue,
  variable: IConfigurationVariable
): boolean => {
  const featureOrOptionId = getFeatureId(value.value, variable);
  const isOptionalItem = checkIsMarkedOptional(featureOrOptionId, variable);
  return isValidAssignedValue(value) || !!isOptionalItem;
};

/**
 * Appends price to values and calculates variable's price
 * @param {IConfigurationVariable} variable the variable to append price to
 * @returns {void}
 */
const appendVariablePrice = (variable: IConfigurationVariable) => {
  let price = 0;

  if (!variable?.values) {
    // In case of string, variable will not have values, so we need to calculate price based on variable price and return
    return variable.price;
  }

  // Filtering assigned values and setting price and name to return array of assigned values with price
  variable.values.forEach((val) => {
    if (variable.variableType === EVariableType.Number) {
      variable.price = variable.price ? Number(val.value) * variable.price : 0;
      price += variable.price;
      return price;
    } else {
      price += val.price ? val.price : 0;
    }

    return price;
  });
  return price;
};

/**
 * Generates the model path based on the id of given section
 * @param {ISection} section the section to get model path for
 * @returns {string} the model path for the given section
 */
export const getModelPathFromSection = (section: ISection | IDetailedSummary) => {
  const viewId = getViewIdSuffix();
  return (
    section.id
      .split('.')
      .filter((part) => part.endsWith(viewId))
      ?.map((p) => p.replace(viewId, ''))
      .slice(1)
      ?.join('.') || ''
  );
};

/**
 * Returns the first assigned value for the variable
 * @param {IConfigurationVariable} variable the variable to get assigned value of
 * @returns {IConfigurationValue} the first assigned value of the variable
 */
export const getAssignedValue = (variable?: IConfigurationVariable | null) => {
  if (!variable) {
    return null;
  }
  return variable.values.find(isValidAssignedValue);
};

/**
 * Checks if variable is submodel count variable or not
 * @param {IConfigurationVariable | IFeatureOrFamily } variable the variable to check
 * @returns {boolean} whether variable is submodel count variable or not
 */
export const isSubmodelCountVariable = (
  variable: IConfigurationVariable | IFeatureOrFamily | null
) =>
  !!(
    variable?.id?.endsWith(IdFormat.Suffix.SubmodelId) &&
    variable?.properties?.find((p) => p.id === EConfigitProperty.SubModelQty)?.value
  );

/**
 * checks if the isMarkedOptional attribute is t be added to the variable/value, subtracts optional items price and returns if the variable changed
 * @param {IConfigurationVariable} variable the variable to check
 * @returns {boolean} whether the variable changed and is to be updated
 */
export const appendIsMarkedOptionalVariable = (variable: IConfigurationVariable) => {
  let toBeAdded = false;
  const varType = getVariableType(variable);
  const optionalVariable: IConfigurationVariable = {
    ...variable,
    values: [] as IConfigurationValue[],
    isMarkedOptional: false,
    price: 0,
  };

  if (
    varType === EVariableType.Date ||
    varType === EVariableType.Number ||
    varType === EVariableType.String
  ) {
    const isMarkedOptional = checkIsMarkedOptional(variable.id, variable);
    optionalVariable.price = variable.price;
    optionalVariable.isMarkedOptional = isMarkedOptional;
    optionalVariable.values = variable.values;
    variable.isMarkedOptional = isMarkedOptional;
    toBeAdded ||= variable.isMarkedOptional || false;
  } else {
    //In case of single or multiselect variable we need to check each value if it is marked optional and calculate price accordingly
    variable.values.forEach((v) => {
      if (checkIsMarkedOptional(`${v.value}`, variable)) {
        toBeAdded = true;
        optionalVariable.values.push(v);
        v.isMarkedOptional = true;
      }
    });
    variable.values = variable.values.filter((v) => !v.isMarkedOptional);
  }

  return toBeAdded ? optionalVariable : null;
};

//Return value based on type
export function getFeatureValue(value: string, variableType: string): string {
  switch (variableType) {
    case EVariableType.String:
      return ` (${value})`;
    case EVariableType.Number:
      return ` (x${value})`;
    default:
      return value;
  }
}

export function filterOptionalValues(
  variable: IConfigurationVariable,
  showOptional: boolean
): IConfigurationVariable {
  return {
    ...variable,
    values: variable.values.filter((v) => showOptional || !v.isMarkedOptional),
  };
}

// Helper: attach optional content to the result only when present
function attachOptional(optional: ISectionPrice, result: ISectionPrice): void {
  if (optional.variables.length || optional.sections.length) {
    result.optional = optional;
  }
}

// Helper: return section when it has visible data, otherwise null
function finalizeSection(section: ISectionPrice): ISectionPrice | null {
  return section.sections?.length ||
    section.variables?.length ||
    (section.optional && section.optional.variables?.length > 0)
    ? section
    : null;
}

/**
 * - Aggregates variable prices into section.price (excluding optional items)
 * - Extracts optional variables/sections into section.optional with accumulated price
 * - Filters variables to only assigned, non-optional, non-suppressed, non-password, non-count
 * - Prunes empty subsections and returns null when a section has no visible data
 * - Mirrors legacy behavior: parent section does not accumulate price from submodel/scope children
 *
 * Note: This function is pure with respect to the returned object but will not mutate the input section.
 *
 * @param {ISectionPrice} section the section to transform
 * @returns {ISectionPrice | null} the transformed section or null if empty
 */
export function prepareConfigurationSummaryData(
  section: ISectionPrice,
  shortSalesText: Map<string, Map<string, string>>
): ISectionPrice | null {
  // Initialize result and optional containers
  const result = createEmptySection(section, shortSalesText);
  const optionalSection = createEmptyOptionalSection(section);

  // 1) Variables: split into kept and optional, accumulate their prices
  const { variables, variablePrice, optionalVariables } = processVariables(section);
  result.variables = variables;
  result.price += variablePrice;
  optionalSection.variables = optionalVariables;

  // 2) Children: build recursively, add non-submodel/scope prices to parent,
  //    and collect optional child content
  const { children, childPrice, optionalSections } = processChildren(section, shortSalesText);
  result.sections = children;
  result.price += childPrice;
  optionalSection.sections = optionalSections;

  // Attach optional structure when any optional content exists
  attachOptional(optionalSection, result);
  // Prune section if nothing visible remains
  return finalizeSection(result);
}
